/**
 * @file Custom hook for fetching order data.
 * This encapsulates the logic for loading, error handling, and data state.
 */
import { useState, useEffect, useCallback } from 'react';
import { getOrderById } from '../repositories/orderRepository';
import { Image, Customer } from '../models/types';

interface UseOrderReturn {
  images: Image[];
  customer: Customer | null;
  isLoading: boolean;
  error: Error | null;
  total: number;
  retry: () => void;
}

/**
 * Calculates the total price from an array of order items.
 * @param images - Array of images with price and quantity information
 * @returns The total price as a number
 */
const calculateTotal = (images: Image[]): number => {
    return images.reduce((total, image) => {
        const price = parseFloat(image.price) || 0;
        const quantity = Number.parseInt(String(image.quantity), 10);
        const normalizedQuantity = Number.isInteger(quantity) && quantity > 0 ? quantity : 1;

        return total + price * normalizedQuantity;
    }, 0);
};

/**
 * A custom hook to fetch and manage the state for order data.
 * @param orderId - The ID of the order to fetch data for.
 * @returns An object containing the images, customer info, loading state, total price, retry function, and any potential error.
 */
export const useOrder = (orderId: string | undefined): UseOrderReturn => {
    const [images, setImages] = useState<Image[]>([]);
    const [customer, setCustomer] = useState<Customer | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const [total, setTotal] = useState<number>(0);

    const fetchOrderData = useCallback(async () => {
        if (!orderId) {
            setIsLoading(false);
            return;
        };

        setIsLoading(true);
        setError(null);

        try {
            console.log('[Hook] Fetching order data for:', orderId);
            const order = await getOrderById(orderId);

            if (!order) {
                throw new Error("Pedido no encontrado.");
            }

            const orderImages = order.images || [];
            setImages(orderImages);
            setCustomer(order.customer);
            setTotal(calculateTotal(orderImages));
            console.log('[Hook] Order data loaded successfully');
        } catch (err) {
            console.error('[Hook] Error loading order:', err);
            setError(err as Error);
        } finally {
            setIsLoading(false);
        }
    }, [orderId]);

    const retry = () => {
        console.log('[Hook] Retrying order fetch...');
        fetchOrderData();
    };

    useEffect(() => {
        fetchOrderData();
    }, [fetchOrderData]);

    return { images, customer, isLoading, error, total, retry };
};
